import React, { useState, useRef } from 'react';
import InlineAIAssist from './InlineAIAssist';
import { getContextForField } from './AIAssistantContexts';
import { useAutoAIAssist } from '../hooks/useAutoAIAssist';

interface SleekExplainFieldProps {
  label: string;
  placeholder: string;
  helpText?: string;
  value?: string;
  onChange?: (value: string) => void;
  fieldType: string;
}

const SleekExplainField: React.FC<SleekExplainFieldProps> = ({ 
  label, 
  placeholder, 
  helpText, 
  value = '', 
  onChange,
  fieldType 
}) => {
  const [fieldValue, setFieldValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);
  const [showAutoEngageModal, setShowAutoEngageModal] = useState(false);
  const [autoEngageReason, setAutoEngageReason] = useState('');
  const aiAssistRef = useRef<{ handleQuickStart: () => void; isActive: () => boolean }>(null);

  const fieldContext = getContextForField(fieldType);
  
  // Auto AI Assist functionality
  const { isChecking, shouldShowPositiveFeedback } = useAutoAIAssist({
    fieldContext,
    currentValue: fieldValue,
    onAutoEngage: (reason) => {
      // Only show auto-engagement modal if AI Assistant is not already active
      if (!aiAssistRef.current?.isActive()) {
        setAutoEngageReason(reason);
        setShowAutoEngageModal(true);
      }
    },
    onPositiveFeedback: () => {
      // Handle positive feedback animation
      console.log('Positive feedback triggered');
    },
    isAIAssistActive: () => aiAssistRef.current?.isActive() || false
  });

  const handleValueChange = (newValue: string) => {
    setFieldValue(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  const getWordCount = () => {
    return fieldValue.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  const getCharacterCount = () => {
    return fieldValue.length;
  };

  const handleAutoEngageAccept = () => {
    setShowAutoEngageModal(false);
    aiAssistRef.current?.handleQuickStart();
  };

  const handleAutoEngageDecline = () => {
    setShowAutoEngageModal(false);
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
        </label>
        
        <div className={`relative transition-all duration-200 ${isFocused ? 'ring-2 ring-blue-500 ring-opacity-20' : ''}`}>
          <textarea
            value={fieldValue}
            onChange={(e) => handleValueChange(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            rows={4}
            className="w-full px-3 py-3 pr-20 pb-8 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 resize-none"
            placeholder={placeholder}
          />
          
          {/* AI Assist Button - Overlay in upper right */}
          <button
            onClick={() => aiAssistRef.current?.handleQuickStart()}
            className="absolute top-2 right-2 inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-md hover:bg-blue-700 transition-colors shadow-sm"
            title="AI Assistant"
          >
            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            AI Assist
          </button>

          {/* Auto-check status indicator */}
          {isChecking && (
            <div className="absolute top-2 right-20 inline-flex items-center px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-md">
              <svg className="w-3 h-3 mr-1 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Checking...
            </div>
          )}

          {/* Positive feedback indicator */}
          {shouldShowPositiveFeedback && (
            <div className="absolute top-2 right-20 inline-flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs rounded-md animate-pulse">
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Looks good!
            </div>
          )}
          
          {/* Character count indicator */}
          <div className="absolute bottom-2 right-3 text-xs text-gray-400">
            {getWordCount()} words • {getCharacterCount()} chars
          </div>
        </div>

        {/* Field status indicator */}
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center space-x-4">
            {fieldValue.length > 0 && (
              <div className="flex items-center text-xs">
                <div className={`w-2 h-2 rounded-full mr-1.5 ${
                  getWordCount() < 10 ? 'bg-red-400' :
                  getWordCount() < 25 ? 'bg-yellow-400' :
                  'bg-green-400'
                }`}></div>
                <span className="text-gray-600">
                  {getWordCount() < 10 ? 'Too brief' :
                   getWordCount() < 25 ? 'Good length' :
                   'Comprehensive'}
                </span>
              </div>
            )}
            
            {helpText && (
              <p className="text-xs text-gray-500">{helpText}</p>
            )}
          </div>
          
          {/* Best practices hint */}
          {fieldValue.length === 0 && (
            <div className="text-xs text-blue-600 flex items-center">
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Be specific and complete
            </div>
          )}
        </div>
      </div>

      {/* Inline AI Assist */}
      <InlineAIAssist
        ref={aiAssistRef}
        fieldContext={fieldContext}
        currentValue={fieldValue}
        onUpdate={handleValueChange}
        placeholder={placeholder}
      />

      {/* Field-specific guidance */}
      {fieldValue.length > 0 && fieldContext.warningFlags.length > 0 && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start">
            <svg className="w-4 h-4 text-amber-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.728-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div>
              <h4 className="text-xs font-medium text-amber-900 mb-1">Avoid These Common Issues:</h4>
              <ul className="text-xs text-amber-800 space-y-0.5">
                {fieldContext.warningFlags.slice(0, 2).map((flag, index) => (
                  <li key={index}>• {flag}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Auto-Engagement Modal */}
      {showAutoEngageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center mb-4">
                <div className="flex items-center justify-center w-8 h-8 bg-orange-100 rounded-full mr-3">
                  <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">AI Assistant Suggestion</h3>
                  <p className="text-sm text-gray-500">Your response could be improved</p>
                </div>
              </div>

              {/* Content */}
              <div className="mb-6">
                <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg mb-4">
                  <p className="text-sm text-orange-800">
                    <strong>Why AI Assist is engaging:</strong> {autoEngageReason}
                  </p>
                </div>
                <p className="text-sm text-gray-700">
                  The AI Assistant can help you create a more comprehensive response that meets 
                  security clearance requirements and reduces the risk of rejection.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleAutoEngageDecline}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Continue Writing
                </button>
                <button
                  onClick={handleAutoEngageAccept}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Get AI Help
                </button>
              </div>

              {/* Additional info */}
              <div className="mt-4 pt-3 border-t border-gray-200">
                <p className="text-xs text-gray-500">
                  You can disable auto-checking in Settings if you prefer manual AI assistance.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SleekExplainField;